import type { CommonData } from '@/components/blockEditor/blockEditor.types';
import { CURRENT_SCHEMA_VERSION } from '@/components/blockEditor/blockEditor.types';

export function getDefaultTrustBasicsData(): CommonData {
  return {
    version: CURRENT_SCHEMA_VERSION,
    blocks: [
      {
        id: 'description',
        type: 'ListItemsSection',
        props: {},
        children: {
          default: [
            {
              id: 'trust-basics-heading',
              type: 'EducationalSlideHeading',
              props: {
                title: 'What is a trust?',
                variant: 'h3',
              },
            },
            {
              id: 'trust-basics-body',
              type: 'EditableEducationalSlideBody',
              props: {
                text: "A trust is a legal arrangement where one party (the grantor) transfers assets to another party (the trustee) to hold and manage for the benefit of a third party (the beneficiary). In some cases, the grantor, trustee, and beneficiary roles can overlap - they don't necessarily have to be three separate people, but the roles are distinct. Trusts can be structured in various ways depending on the grantor's goals, with the two main categories being revocable and irrevocable trusts.",
              },
            },
          ],
        },
      },
      {
        id: 'trust-types-and-benefits',
        type: 'ListItemsSection',
        props: {},
        children: {
          default: [
            {
              id: 'trust-types-columns',
              type: 'TwoColumnLayout',
              props: {
                leftWidth: '50%',
                rightWidth: '50%',
                columnGap: 5,
                showDivider: true,
              },
              children: {
                leftColumn: [
                  {
                    id: 'revocable-trusts-heading',
                    type: 'EducationalSlideHeading',
                    props: {
                      title: 'Revocable trusts',
                      variant: 'h2',
                    },
                  },
                  {
                    id: 'revocable-trusts-body',
                    type: 'EditableEducationalSlideBody',
                    props: {
                      text: 'A revocable trust (sometimes also called a revocable living trust) is a legal entity created by an individual (the grantor) to hold and manage assets during their lifetime and distribute them after death - while retaining the right to modify or revoke the trust at any time.',
                    },
                  },
                  {
                    id: 'item-changeable',
                    type: 'PresentationListItem',
                    props: {
                      title: 'Easily changeable',
                      description:
                        'The grantor can change the terms, remove assets, or terminate the trust entirely during their lifetime',
                      iconType: 'shield',
                      iconColor: 'PRIMARY',
                    },
                  },
                  {
                    id: 'item-probate',
                    type: 'PresentationListItem',
                    props: {
                      title: 'Avoids probate',
                      description:
                        'Assets held in a revocable trust generally do not go through probate, allowing for more privacy and faster estate administration',
                      iconType: 'shield',
                      iconColor: 'PRIMARY',
                    },
                  },
                  {
                    id: 'item-no-protection',
                    type: 'PresentationListItem',
                    props: {
                      title: 'No asset protection',
                      description:
                        'Since the grantor retains control, the assets are still considered part of their estate for creditor claims and estate tax purposes',
                      iconType: 'flag',
                      iconColor: 'NEGATIVE',
                    },
                  },
                  {
                    id: 'item-irrevocable',
                    type: 'PresentationListItem',
                    props: {
                      title: 'Becomes irrevocable at death',
                      description:
                        "At the grantor's death, the trust typically becomes irrevocable and its terms govern how assets are distributed",
                      iconType: 'flag',
                      iconColor: 'NEGATIVE',
                    },
                  },
                ],
                rightColumn: [
                  {
                    id: 'irrevocable-trusts-heading',
                    type: 'EducationalSlideHeading',
                    props: {
                      title: 'Irrevocable trusts',
                      variant: 'h2',
                    },
                  },
                  {
                    id: 'irrevocable-trusts-body',
                    type: 'EditableEducationalSlideBody',
                    props: {
                      text: 'An irrevocable trust is a legal entity created by an individual (the grantor) to hold and manage assets, in which the grantor permanently gives up control and the ability to modify or revoke the trust once it is established (although they may retain some control through the terms in the trust document).',
                    },
                  },
                  {
                    id: 'item-tax',
                    type: 'PresentationListItem',
                    props: {
                      title: 'Tax planning tool',
                      description:
                        'Often used to reduce estate and gift taxes, shift income, or facilitate charitable or generational wealth planning',
                      iconType: 'shield',
                      iconColor: 'PRIMARY',
                    },
                  },
                  {
                    id: 'item-protection',
                    type: 'PresentationListItem',
                    props: {
                      title: 'Asset protection',
                      description:
                        "Assets in the trust are generally protected from the grantor's creditors and excluded from the grantor's taxable estate",
                      iconType: 'shield',
                      iconColor: 'PRIMARY',
                    },
                  },
                  {
                    id: 'item-not-changeable',
                    type: 'PresentationListItem',
                    props: {
                      title: 'Not easily changeable',
                      description:
                        'Once created and funded, the grantor generally cannot change the trust in any significant way (without beneficiary and/or court consent, where applicable)',
                      iconType: 'flag',
                      iconColor: 'NEGATIVE',
                    },
                  },
                ],
              },
            },
          ],
        },
      },
    ],
  };
}
